import { assertNever } from '@framework/engine';
import { t } from '@framework/i18n';
import clx from 'classnames';
import styled from 'styled-components';
import { SubmissionModuleState } from '../../fsm/SubmissionModuleState';

function getModuleValidationStateText(
  state?: SubmissionModuleState,
): React.ReactNode {
  if (!state) return null;
  switch (state) {
    case SubmissionModuleState.Compilation:
      return t('module-validation-state-text.compila', 'Compila');
    case SubmissionModuleState.Completed:
      return t('module-validation-state-text.completato', 'Completato');
    case SubmissionModuleState.Sign:
      return t('module-validation-state-text.firma', 'Firma');
    case SubmissionModuleState.None:
      return '';
  }
  assertNever(state);
}

const stateSequence = [
  SubmissionModuleState.None,
  SubmissionModuleState.Compilation,
  SubmissionModuleState.Sign,
  SubmissionModuleState.Completed,
];

export function getActiveFromStateProps(
  params: IModuleStateProgressBarItemProps,
) {
  if (!params.current || !params.state) {
    return false;
  }
  return (
    stateSequence.indexOf(params.state) ==
    Math.max(stateSequence.indexOf(params.current), 1)
  );
}

export function getDoneFromStateProps(
  params: IModuleStateProgressBarItemProps,
) {
  if (!params.current || !params.state) {
    return false;
  }
  return (
    stateSequence.indexOf(params.state) <
    Math.max(stateSequence.indexOf(params.current), 1)
  );
}

const StatusWrapper = styled.div`
  display: inline-flex;
  position: relative;
  padding: 0 6px;
  text-transform: uppercase;
  font-size: 13px;
  background-color: #f0f0f0;
  color: ${props => props.theme.colorTextSecondary};
  /* margin-right: 10px; */
  padding-left: 12px;
  padding-right: 3px;
  height: 20px;
  &:first-child {
    border-radius: ${props => props.theme.borderRadius}px;
    /* border-radius: 4px 0 0 4px; */
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding-left: 6px;
  }
  &::after {
    position: absolute;
    top: 0px;
    right: -6px;
    height: 100%;
    content: '';
    display: inline-block;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 6px solid #f0f0f0;
    margin-left: 6px;
    z-index: 2;
  }
  &::before {
    position: absolute;
    top: 0px;
    right: -7px;
    height: 100%;
    content: '';
    display: inline-block;
    width: 2px;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-left: 6px solid white;
    margin-left: 6px;
    z-index: 1;
  }
  &:last-child {
    border-radius: ${props => props.theme.borderRadius}px;
    /* border-radius: 0 4px 4px 0; */
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    padding-right: 6px;
    &::after {
      border: none;
      content: none;
    }
    &::before {
      border: none;
      content: none;
    }
  }
  &.done {
    background-color: ${props => props.theme.moduleStateValidColor};
    color: white;
    &::after {
      border-left-color: ${props => props.theme.moduleStateValidColor};
    }
  }
  &.active {
    background-color: ${props => props.theme.colorPrimary};
    color: white;
    &::after {
      border-left-color: ${props => props.theme.colorPrimary};
    }
    &.error {
      background-color: ${props => props.theme.moduleStateErrorColor};
      &::after {
        border-left-color: ${props => props.theme.moduleStateErrorColor};
      }
    }
    &.valid {
      background-color: ${props => props.theme.moduleStateValidColor};
      &::after {
        border-left-color: ${props => props.theme.moduleStateValidColor};
      }
    }
    &.sign {
      background-color: ${props => props.theme.moduleStateSignColor};
      &::after {
        border-left-color: ${props => props.theme.moduleStateSignColor};
      }
    }
    &.completed {
      background-color: ${props => props.theme.moduleStateCompletedColor};
      &::after {
        border-left-color: ${props => props.theme.moduleStateCompletedColor};
      }
    }
    &.compilation {
      background-color: ${props => props.theme.colorPrimary};
      &::after {
        border-left-color: ${props => props.theme.colorPrimary};
      }
    }
  }
`;

export interface IModuleStateProgressBarItemProps {
  state: SubmissionModuleState;
  current?: SubmissionModuleState;
  isValid?: boolean | null | undefined;
}

/**
 * Singolo elemento della Progress bar di stato di un modulo durante la compilazione.
 */
export function ModuleStateProgressBarItem(
  props: IModuleStateProgressBarItemProps,
) {
  const { state, current, isValid } = props;
  const active = getActiveFromStateProps({ ...props });

  return (
    <StatusWrapper
      role="listitem"
      aria-current={active ? 'step' : undefined}
      className={clx(`${current}`, {
        active,
        done: getDoneFromStateProps({ ...props }),
        error: isValid === false,
        valid: isValid === true,
      })}
    >
      {getModuleValidationStateText(state)}
    </StatusWrapper>
  );
}
